import { ApiError } from "@shared-core/data/api/errors";
import { extractFieldErrors } from "@shared-core/data/validation";

/** A refused campaign action, read for the page: the field errors keyed by
 *  field (shown beside their input) and one line for the banner. A plain
 *  400/409 from the server carries a reason worth showing as-is; anything
 *  else gets the caller's fallback. */
export interface CampaignActionError {
  fields: Record<string, string>;
  summary: string;
}

export function readCampaignActionError(
  e: unknown,
  fallback: string,
): CampaignActionError {
  const fieldErrors = extractFieldErrors(e);
  if (fieldErrors.length > 0) {
    const fields: Record<string, string> = {};
    for (const error of fieldErrors) fields[error.field] ??= error.message;
    return {
      fields,
      summary:
        fieldErrors.length === 1
          ? fieldErrors[0].message
          : `${fieldErrors.length} things need fixing: ${fieldErrors.map((f) => f.message).join("; ")}`,
    };
  }
  if (
    e instanceof ApiError &&
    (e.status === 400 || e.status === 409) &&
    e.detail
  ) {
    return { fields: {}, summary: e.detail };
  }
  return { fields: {}, summary: fallback };
}

/** The structured log fields for a failed campaign action (never the
 *  error text, which can carry user content). */
export function failureLogFields(
  e: unknown,
  ids: Record<string, string | undefined>,
): Record<string, unknown> {
  return {
    ...ids,
    status: e instanceof ApiError ? e.status : undefined,
    exc_type: e instanceof Error ? e.name : typeof e,
  };
}
